/**
 * Invoices – Search by patient and view one invoice per visit (JMD).
 * Supports per-line-item discounts on open visits.
 */

import { useState } from 'react';
import DashboardLayout from '@/pages/dashboard/components/DashboardLayout';
import { patientsApi, visitsApi } from '@/services/api';
import type { IssueInvoice, InvoiceLineItem, Patient, Visit } from '@/types/index.ts';
import { handleApiError } from '@/utils/errorHandler';

function formatJmd(amount: number): string {
  return `JMD ${Number(amount).toLocaleString('en-JM', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
}

function formatDiscount(line: InvoiceLineItem): string {
  if (!line.discount_type || line.discount_value == null) return '—';
  return line.discount_type === 'percentage'
    ? `${line.discount_value}%`
    : formatJmd(line.discount_value);
}

// ---- Inline discount editor for a single line item ----
interface DiscountEditorProps {
  visitId: number;
  line: InvoiceLineItem;
  onSaved: (updatedVisit: Visit) => void;
}

function DiscountEditor({ visitId, line, onSaved }: DiscountEditorProps) {
  const [type, setType] = useState<'percentage' | 'fixed'>(line.discount_type ?? 'percentage');
  const [value, setValue] = useState<string>(line.discount_value != null ? String(line.discount_value) : '');
  const [saving, setSaving] = useState(false);
  const [removing, setRemoving] = useState(false);
  const [err, setErr] = useState<string | null>(null);

  const hasDiscount = line.discount_type != null;

  const handleSave = async () => {
    const numVal = parseFloat(value);
    if (isNaN(numVal) || numVal < 0) { setErr('Enter a valid amount'); return; }
    if (type === 'percentage' && numVal > 100) { setErr('Max 100%'); return; }
    if (!line.item_id) { setErr('Item ID missing'); return; }
    setSaving(true);
    setErr(null);
    try {
      const res = await visitsApi.updateLineDiscount(visitId, {
        item_id: line.item_id,
        unit_price: line.unit_price,
        discount_type: type,
        discount_value: numVal,
      });
      const updated = (res as any)?.data as Visit;
      onSaved(updated);
    } catch (e) {
      setErr(handleApiError(e, 'Discount').message);
    } finally {
      setSaving(false);
    }
  };

  const handleRemove = async () => {
    if (!line.item_id) return;
    setRemoving(true);
    setErr(null);
    try {
      const res = await visitsApi.removeLineDiscount(visitId, line.item_id, line.unit_price);
      const updated = (res as any)?.data as Visit;
      onSaved(updated);
    } catch (e) {
      setErr(handleApiError(e, 'Remove discount').message);
    } finally {
      setRemoving(false);
    }
  };

  return (
    <div className="flex flex-col gap-1 min-w-[180px]">
      <div className="flex items-center gap-1">
        <select
          value={type}
          onChange={(e) => setType(e.target.value as 'percentage' | 'fixed')}
          className="px-1.5 py-1 text-xs border border-gray-300 rounded-sm focus:outline-none focus:ring-1 focus:ring-[#0997a4] bg-white"
        >
          <option value="percentage">%</option>
          <option value="fixed">JMD fixed</option>
        </select>
        <input
          type="number"
          min={0}
          max={type === 'percentage' ? 100 : undefined}
          step="0.01"
          value={value}
          onChange={(e) => setValue(e.target.value)}
          placeholder={type === 'percentage' ? '0–100' : 'Amount'}
          className="w-20 px-1.5 py-1 text-xs border border-gray-300 rounded-sm focus:outline-none focus:ring-1 focus:ring-[#0997a4]"
        />
        <button
          type="button"
          onClick={handleSave}
          disabled={saving}
          className="px-2 py-1 text-xs font-medium bg-[#0997a4] text-white rounded-sm hover:bg-[#087d89] disabled:opacity-60"
        >
          {saving ? '…' : 'Apply'}
        </button>
        {hasDiscount && (
          <button
            type="button"
            onClick={handleRemove}
            disabled={removing}
            title="Remove discount"
            className="px-1.5 py-1 text-xs text-red-500 hover:text-red-700 disabled:opacity-50"
          >
            <i className="ri-close-line" aria-hidden />
          </button>
        )}
      </div>
      {err && <p className="text-xs text-red-600">{err}</p>}
    </div>
  );
}

// ---- Main page ----
export default function InvoicesPage() {
  const [searchQuery, setSearchQuery] = useState('');
  const [patientSearchResults, setPatientSearchResults] = useState<Patient[]>([]);
  const [selectedPatient, setSelectedPatient] = useState<Patient | null>(null);
  const [visits, setVisits] = useState<Visit[]>([]);
  const [isSearching, setIsSearching] = useState(false);
  const [isLoadingVisits, setIsLoadingVisits] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const handlePatientSearch = async () => {
    const q = searchQuery.trim();
    if (!q) { setError('Enter patient name or ID to search'); return; }
    setError(null);
    setIsSearching(true);
    setPatientSearchResults([]);
    setSelectedPatient(null);
    setVisits([]);
    try {
      const res = (await patientsApi.getPatients({ search: q, per_page: 20 })) as unknown as { data?: Patient[] | { data?: Patient[] } };
      const raw = res?.data;
      const list = Array.isArray(raw) ? raw : (raw && typeof raw === 'object' && 'data' in raw ? (raw as { data?: Patient[] }).data : null) ?? [];
      setPatientSearchResults(list);
      if (list.length === 0) setError('No patients found. Try a different search.');
    } catch (err) {
      setError(handleApiError(err, 'Patient search').message);
    } finally {
      setIsSearching(false);
    }
  };

  const handleSelectPatient = async (patient: Patient) => {
    setSelectedPatient(patient);
    setError(null);
    setIsLoadingVisits(true);
    setVisits([]);
    try {
      const res = (await visitsApi.getVisits({ patient_id: patient.id, per_page: 100 })) as unknown as { data?: Visit[] | { data?: Visit[] } };
      const raw = res?.data;
      const list = Array.isArray(raw) ? raw : (raw && typeof raw === 'object' && 'data' in raw ? (raw as { data?: Visit[] }).data : null) ?? [];
      setVisits(list);
      if (list.length === 0) setError('No visit invoices found for this patient.');
    } catch (err) {
      setError(handleApiError(err, 'Invoices').message);
    } finally {
      setIsLoadingVisits(false);
    }
  };

  // Called by DiscountEditor after a successful save/remove
  const handleVisitUpdated = (updatedVisit: Visit) => {
    setVisits((prev) => prev.map((v) => (v.id === updatedVisit.id ? updatedVisit : v)));
  };

  const handlePrintInvoice = async (visit: Visit) => {
    const patientName = visit.patient?.name ?? selectedPatient?.name ?? 'Patient';
    const ref = visit.reference_number;
    const invoice: IssueInvoice | undefined = visit.invoice;
    const dateOpened = visit.created_at ? new Date(visit.created_at).toLocaleString() : '';
    const dateClosed = visit.closed_at ? new Date(visit.closed_at).toLocaleString() : 'Still open';
    const visitTypeLabel = visit.visit_type === 'inpatient' ? 'Inpatient' : 'Outpatient';

    const hasAnyDiscount = (invoice?.line_items ?? []).some((l) => l.discount_type);

    const lineRows = (invoice?.line_items ?? []).map((line) => {
      const discountCell = hasAnyDiscount
        ? `<td style="padding:6px 8px; text-align:right; color:#b45309;">${formatDiscount(line)}</td>`
        : '';
      const totalValue = line.discounted_total != null ? line.discounted_total : line.line_total;
      return `<tr>
        <td style="padding:6px 8px;">${line.name}</td>
        <td style="padding:6px 8px; text-align:right;">${line.quantity} ${line.unit}</td>
        <td style="padding:6px 8px; text-align:right;">${formatJmd(line.unit_price)}</td>
        ${discountCell}
        <td style="padding:6px 8px; text-align:right;">${formatJmd(totalValue)}</td>
      </tr>`;
    }).join('');

    const discountHeaderCell = hasAnyDiscount ? '<th style="text-align:right; padding:8px;">Discount</th>' : '';
    const subtotal = invoice?.subtotal ?? invoice?.total ?? 0;
    const totalDiscounts = invoice?.total_discounts ?? 0;
    const total = invoice?.total ?? 0;

    const discountSummaryRow = totalDiscounts > 0 ? `
      <tr>
        <td colspan="${hasAnyDiscount ? 4 : 3}" style="padding:8px; text-align:right; color:#b45309;">Discounts</td>
        <td style="padding:8px; text-align:right; color:#b45309;">− ${formatJmd(totalDiscounts)}</td>
      </tr>` : '';

    // Fetch logo and encode as base64 so the print popup has no external dependency
    let logoHtml = '';
    try {
      const res = await fetch('/images/xtraCare_logo.png');
      const blob = await res.blob();
      const dataUrl = await new Promise<string>((resolve, reject) => {
        const reader = new FileReader();
        reader.onload = () => resolve(reader.result as string);
        reader.onerror = reject;
        reader.readAsDataURL(blob);
      });
      logoHtml = `<img src="${dataUrl}" alt="Logo" style="height:60px; object-fit:contain;" />`;
    } catch {
      // logo unavailable — header renders without it
    }

    const html = `<!DOCTYPE html>
<html>
  <head><title>Invoice ${ref}</title></head>
  <body style="font-family: system-ui, sans-serif; padding: 24px; max-width: 700px; margin: 0 auto;">
    <div style="display:flex; justify-content:space-between; align-items:center;">
      <div>
        <h2 style="color: #0997a4; margin:0 0 4px;">Visit Invoice</h2>
        <p style="margin:0; color:#666; font-size:13px;">${visitTypeLabel} Visit</p>
      </div>
      ${logoHtml}
    </div>
    <hr style="margin: 16px 0;">
    <p style="margin:4px 0;"><strong>Patient:</strong> ${patientName}</p>
    <p style="margin:4px 0;"><strong>Reference:</strong> ${ref}</p>
    <p style="margin:4px 0;"><strong>Opened:</strong> ${dateOpened}</p>
    <p style="margin:4px 0;"><strong>Closed:</strong> ${dateClosed}</p>
    <p style="color:#666; font-size:12px; margin-top:8px;">All amounts in Jamaican Dollars (JMD)</p>
    <table style="width:100%; border-collapse: collapse; margin-top:16px; font-size:14px;">
      <thead>
        <tr style="border-bottom:2px solid #ddd; background:#f9f9f9;">
          <th style="text-align:left; padding:8px;">Item</th>
          <th style="text-align:right; padding:8px;">Qty</th>
          <th style="text-align:right; padding:8px;">Unit price</th>
          ${discountHeaderCell}
          <th style="text-align:right; padding:8px;">Total</th>
        </tr>
      </thead>
      <tbody>${lineRows}</tbody>
      <tfoot>
        ${totalDiscounts > 0 ? `<tr><td colspan="${hasAnyDiscount ? 4 : 3}" style="padding:8px; text-align:right; color:#555;">Subtotal</td><td style="padding:8px; text-align:right; color:#555;">${formatJmd(subtotal)}</td></tr>` : ''}
        ${discountSummaryRow}
        <tr style="border-top:2px solid #ddd;">
          <td colspan="${hasAnyDiscount ? 4 : 3}" style="padding:8px; text-align:right; font-weight:600;">Total</td>
          <td style="padding:8px; text-align:right; font-weight:600;">${formatJmd(total)}</td>
        </tr>
      </tfoot>
    </table>
  </body>
</html>`;

    const w = window.open('', '_blank');
    if (w) { w.document.write(html); w.document.close(); w.print(); w.close(); }
  };

  return (
    <DashboardLayout>
      <div className="space-y-6">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Invoices</h1>
          <p className="text-sm text-gray-600 mt-1">Search by patient name or MRN to see invoice.</p>
        </div>

        {/* Patient search */}
        <div className="flex gap-3 flex-wrap">
          <input
            type="text"
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            onKeyDown={(e) => e.key === 'Enter' && handlePatientSearch()}
            placeholder="Search by patient name or MRN"
            className="flex-1 min-w-[200px] px-4 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-[#0997a4]"
          />
          <button
            type="button"
            onClick={handlePatientSearch}
            disabled={isSearching}
            className="px-4 py-2 bg-[#0997a4] text-white rounded-lg text-sm font-medium hover:bg-[#087d89] disabled:opacity-60 transition-colors"
          >
            {isSearching ? 'Searching…' : 'Search'}
          </button>
        </div>

        {patientSearchResults.length > 0 && (
          <div className="mt-2">
            <p className="text-sm text-gray-600 mb-2">Select a patient:</p>
            <ul className="space-y-1 border border-gray-200 rounded-lg divide-y divide-gray-100 overflow-hidden">
              {patientSearchResults.map((p) => (
                <li key={p.id}>
                  <button
                    type="button"
                    onClick={() => handleSelectPatient(p)}
                    className={`w-full text-left px-4 py-3 text-sm hover:bg-gray-50 transition-colors flex justify-between items-center ${
                      selectedPatient?.id === p.id ? 'bg-[#0997a4]/10 text-[#0997a4]' : 'text-gray-900'
                    }`}
                  >
                    <span className="font-medium">{p.name}</span>
                    {p.identifier && <span className="text-gray-500 text-xs">{p.identifier}</span>}
                  </button>
                </li>
              ))}
            </ul>
          </div>
        )}

        {error && (
          <div className="bg-amber-50 border-l-4 border-l-amber-500 border border-amber-200 rounded-sm px-3 py-2 flex items-center gap-2">
            <i className="ri-error-warning-fill text-amber-500 text-base flex-shrink-0" aria-hidden />
            <p className="text-xs font-medium text-amber-800 flex-1">{error}</p>
            <button type="button" onClick={() => setError(null)} className="text-amber-500 hover:text-amber-700 ml-1 flex-shrink-0">
              <i className="ri-close-line text-sm" aria-hidden />
            </button>
          </div>
        )}

        {/* Visit invoices for selected patient */}
        {selectedPatient && (
          <div className="bg-white rounded-lg border border-gray-200 p-6">
            <h2 className="text-lg font-semibold text-gray-900 mb-4">
              Invoice for {selectedPatient.name}
              {selectedPatient.identifier && (
                <span className="text-gray-500 font-normal text-sm ml-2">({selectedPatient.identifier})</span>
              )}
            </h2>

            {isLoadingVisits ? (
              <div className="py-8 text-center text-gray-500">
                <i className="ri-loader-4-line animate-spin text-2xl" aria-hidden />
                <p className="mt-2">Loading invoices…</p>
              </div>
            ) : visits.length === 0 ? (
              <p className="text-gray-500 text-sm py-4">No invoices to show.</p>
            ) : (
              <ul className="space-y-4">
                {visits.map((visit) => {
                  const inv = visit.invoice;
                  const dateOpened = visit.created_at ? new Date(visit.created_at).toLocaleString() : '—';
                  const isOpen = visit.status === 'open';
                  const hasDiscounts = (inv?.total_discounts ?? 0) > 0;

                  return (
                    <li key={visit.id} className="border border-gray-200 rounded-lg overflow-hidden">
                      {/* Header */}
                      <div className="px-4 py-3 bg-gray-50 border-b border-gray-200 flex flex-wrap items-center justify-between gap-2">
                        <div className="flex items-center gap-3 flex-wrap">
                          <span className="font-mono text-sm font-medium text-gray-900">{visit.reference_number}</span>
                          <span className="text-sm text-gray-500">{dateOpened}</span>
                          <span className="text-xs capitalize text-gray-500 bg-gray-100 px-2 py-0.5 rounded-full">
                            {visit.visit_type}
                          </span>
                          <span className={`text-xs font-medium px-2 py-0.5 rounded-full ${isOpen ? 'bg-green-100 text-green-700' : 'bg-gray-200 text-gray-600'}`}>
                            {isOpen ? 'Open' : 'Closed'}
                          </span>
                        </div>
                        <div className="flex items-center gap-2">
                          {inv && !Array.isArray(inv) && typeof inv.total === 'number' && (
                            <span className="text-sm font-semibold text-gray-900">{formatJmd(inv.total)}</span>
                          )}
                          <button
                            type="button"
                            onClick={() => handlePrintInvoice(visit)}
                            className="px-3 py-1.5 text-sm font-medium text-[#0997a4] hover:bg-[#0997a4]/10 rounded-lg transition-colors"
                          >
                            <i className="ri-printer-line mr-1" aria-hidden />
                            Print
                          </button>
                        </div>
                      </div>

                      {/* Line items */}
                      <div className="p-4">
                        {inv && Array.isArray(inv.line_items) && inv.line_items.length > 0 ? (
                          <>
                            <table className="w-full text-sm">
                              <thead>
                                <tr className="border-b border-gray-200 text-gray-600">
                                  <th className="text-left py-2 font-medium">Item</th>
                                  <th className="text-right py-2 font-medium">Qty</th>
                                  <th className="text-right py-2 font-medium">Unit price</th>
                                  <th className="text-right py-2 font-medium">Subtotal</th>
                                  <th className="text-right py-2 font-medium w-52">
                                    {isOpen ? (
                                      <span className="flex items-center justify-end gap-1">
                                        <i className="ri-percent-line text-amber-500" aria-hidden />
                                        Discount
                                      </span>
                                    ) : 'Discount'}
                                  </th>
                                  <th className="text-right py-2 font-medium">Total</th>
                                </tr>
                              </thead>
                              <tbody>
                                {inv.line_items.map((line, i) => (
                                  <tr key={i} className="border-b border-gray-100">
                                    <td className="py-2 text-gray-900">{line.name}</td>
                                    <td className="py-2 text-right text-gray-700">{line.quantity} {line.unit}</td>
                                    <td className="py-2 text-right text-gray-700">{formatJmd(line.unit_price)}</td>
                                    <td className="py-2 text-right text-gray-500">{formatJmd(line.line_total)}</td>
                                    <td className="py-2 text-right">
                                      {isOpen ? (
                                        <DiscountEditor
                                          visitId={visit.id}
                                          line={line}
                                          onSaved={handleVisitUpdated}
                                        />
                                      ) : (
                                        <span className={line.discount_type ? 'text-amber-700 font-medium' : 'text-gray-400'}>
                                          {formatDiscount(line)}
                                        </span>
                                      )}
                                    </td>
                                    <td className="py-2 text-right font-medium text-gray-900">
                                      {formatJmd(line.discounted_total ?? line.line_total)}
                                    </td>
                                  </tr>
                                ))}
                              </tbody>
                            </table>

                            {/* Totals summary */}
                            <div className="mt-3 space-y-1 text-sm text-right">
                              {hasDiscounts && (
                                <>
                                  <p className="text-gray-500">
                                    Subtotal: <span className="font-medium text-gray-700">{formatJmd(inv.subtotal)}</span>
                                  </p>
                                  <p className="text-amber-700">
                                    Discounts: <span className="font-medium">− {formatJmd(inv.total_discounts ?? 0)}</span>
                                  </p>
                                </>
                              )}
                              <p className="text-gray-900 font-semibold">
                                Total: {formatJmd(inv.total)}
                              </p>
                            </div>
                          </>
                        ) : (
                          <p className="text-sm text-gray-400 italic">No medications issued yet for this visit.</p>
                        )}
                      </div>
                    </li>
                  );
                })}
              </ul>
            )}
          </div>
        )}
      </div>
    </DashboardLayout>
  );
}
